export interface RecoveryReading {
  recoveryId: number;
  organisationId: number;
  firstName: string;
  lastName: string;
  cycleId: number;
  date: Date;
  dayOfWeek: string;
  restingHeartRate: number;
  heartRateVariability: number;
  recovery: number;
  sleepScore: number;
  sleepOnset: Date;
  wakeOnset: Date;
  hoursInBed: number;
  hoursOfSleep: number;
  sleepNeed: number;
  sleepEfficiency: number;
  wakePeriods: number;
  sleepDisturbances: number;
  latency: number;
  cycles: number;
  remSleepHours: number;
  deeplSleepHours: number;
  lightSleepHours: number;
  awakeHours: number;
  missingDataHours: number;
  sleepDebtHours: number;
  respiratoryRate: number;
  lastNapEndTime: Date;
  napTimeInBed: number;
  totalCycleNapTime: number;
  napDisturbances: number;
  napRemHours: number;
  napSwsHours: number;
  napLightHours: number;
  napWakeHours: number;
  napMissingDataHours: number;
  totalCycleTimeHours: number;
  timezoneOffset: string;
  oxygenSaturation: number;
  skinTemp: number;
  optimalSleepOnset: Date;
  optimalWakeOnset: Date;
  remPercentage: number;
  deepSleepPercentage: number;
  restorativeSleepHours: number;
  restorativeSleepPercentage: number;
  sleepConsistencyPercentage: number;
  shipId: number;
}

export interface RecoveryZone {
  min: number;
  max: number;
  label: string;
  color: string;
  description: string;
  recommendation: string;
}

export interface RecoveryMetrics {
  current: number;
  trend: 'improving' | 'declining' | 'stable';
  weeklyAverage: number;
  monthlyAverage: number;
  greenDays: number;
  yellowDays: number;
  redDays: number;
}

export interface RecoveryComponent {
  name: string;
  value: number;
  baseline: number;
  unit: string;
  impact: number;
  color: string;
}

export class RecoveryAnalyzer {
  static getRecoveryZones(): RecoveryZone[] {
    return [
      {
        min: 0, max: 33, label: 'Red', color: '#FF4444',
        description: 'Significantly under-recovered',
        recommendation: 'Prioritize rest and stress management'
      },
      {
        min: 34, max: 66, label: 'Yellow', color: '#FFB347',
        description: 'Adequate recovery with room for improvement',
        recommendation: 'Light to moderate training recommended'
      },
      {
        min: 67, max: 100, label: 'Green', color: '#00C851',
        description: 'Well recovered and ready to perform',
        recommendation: 'Optimal for high-intensity training'
      }
    ];
  }

  static determineZone(recovery: number): RecoveryZone {
    const zones = this.getRecoveryZones();
    return zones.find(zone => recovery >= zone.min && recovery <= zone.max) || zones[0];
  }

  static calculateTrend(data: number[], timeRange: '7d' | '30d' | '90d'): 'improving' | 'declining' | 'stable' {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    if (data.length < 4) return 'stable';
    const recent = data.slice(-7).reduce((a, b) => a + b, 0) / 7;
    const previous = data.slice(-days, -7).reduce((a, b) => a + b, 0) / 7;
    const diff = previous - recent;
    return Math.abs(diff) < 3 ? 'stable' : diff > 0 ? 'improving' : 'declining';
  }

  static formatTime(hours: number): string {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  }

  // Aggregate data by date - fixed to use proper recovery data fields
  static aggregateDataByDate(data: RecoveryReading[]): RecoveryReading[] {
    if (!data || data.length === 0) return [];

    // Group data by date
    const groupedByDate = data.reduce((groups, item) => {
      const dateKey = new Date(item.date).toISOString().split('T')[0];
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
      return groups;
    }, {} as Record<string, RecoveryReading[]>);

    // Aggregate each date group into a single record
    return Object.entries(groupedByDate).map(([dateKey, dayData]) => {
      const validData = dayData.filter(item =>
        item.recovery !== null &&
        item.recovery !== undefined &&
        !isNaN(item.recovery)
      );

      if (validData.length === 0) {
        // Return a default record if no valid data
        return {
          ...dayData[0],
          date: new Date(dateKey),
          recovery: 0,
          restingHeartRate: 0,
          heartRateVariability: 0,
          sleepScore: 0,
          hoursOfSleep: 0
        };
      }

      // Helper function to safely average numeric values
      const safeAverage = (field: keyof RecoveryReading) => {
        const values = validData.map(item => item[field]).filter(val => val !== null && val !== undefined && !isNaN(Number(val)));
        return values.length > 0 ? values.reduce((sum: number, val) => sum + Number(val), 0) / values.length : 0;
      };

      return {
        recoveryId: dayData[0].recoveryId,
        organisationId: dayData[0].organisationId,
        firstName: `${validData.length} crew members`,
        lastName: '',
        cycleId: dayData[0].cycleId,
        date: new Date(dateKey),
        dayOfWeek: dayData[0].dayOfWeek,
        restingHeartRate: safeAverage('restingHeartRate'),
        heartRateVariability: safeAverage('heartRateVariability'),
        recovery: safeAverage('recovery'),
        sleepScore: safeAverage('sleepScore'),
        sleepOnset: dayData[0].sleepOnset,
        wakeOnset: dayData[0].wakeOnset,
        hoursInBed: safeAverage('hoursInBed'),
        hoursOfSleep: safeAverage('hoursOfSleep'),
        sleepNeed: safeAverage('sleepNeed'),
        sleepEfficiency: safeAverage('sleepEfficiency'),
        wakePeriods: safeAverage('wakePeriods'),
        sleepDisturbances: safeAverage('sleepDisturbances'),
        latency: safeAverage('latency'),
        cycles: safeAverage('cycles'),
        remSleepHours: safeAverage('remSleepHours'),
        deeplSleepHours: safeAverage('deeplSleepHours'),
        lightSleepHours: safeAverage('lightSleepHours'),
        awakeHours: safeAverage('awakeHours'),
        missingDataHours: safeAverage('missingDataHours'),
        sleepDebtHours: safeAverage('sleepDebtHours'),
        respiratoryRate: safeAverage('respiratoryRate'),
        lastNapEndTime: dayData[0].lastNapEndTime,
        napTimeInBed: safeAverage('napTimeInBed'),
        totalCycleNapTime: safeAverage('totalCycleNapTime'),
        napDisturbances: safeAverage('napDisturbances'),
        napRemHours: safeAverage('napRemHours'),
        napSwsHours: safeAverage('napSwsHours'),
        napLightHours: safeAverage('napLightHours'),
        napWakeHours: safeAverage('napWakeHours'),
        napMissingDataHours: safeAverage('napMissingDataHours'),
        totalCycleTimeHours: safeAverage('totalCycleTimeHours'),
        timezoneOffset: dayData[0].timezoneOffset,
        oxygenSaturation: safeAverage('oxygenSaturation'),
        skinTemp: safeAverage('skinTemp'),
        optimalSleepOnset: dayData[0].optimalSleepOnset,
        optimalWakeOnset: dayData[0].optimalWakeOnset,
        remPercentage: safeAverage('remPercentage'),
        deepSleepPercentage: safeAverage('deepSleepPercentage'),
        restorativeSleepHours: safeAverage('restorativeSleepHours'),
        restorativeSleepPercentage: safeAverage('restorativeSleepPercentage'),
        sleepConsistencyPercentage: safeAverage('sleepConsistencyPercentage'),
        shipId: dayData[0].shipId
      };
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()); // Sort by date
  }

  static filterDataByTimeRange(data: RecoveryReading[], timeRange: '7d' | '30d' | '90d'): RecoveryReading[] {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const cutoffDate = new Date(
      Math.max(...data.map((obj: RecoveryReading) => new Date(obj.date).getTime()))
    );
    cutoffDate.setDate(cutoffDate.getDate() - days);

    return data.filter(item => new Date(item.date) >= cutoffDate);
  }
}
